import type {
  ApiResponse,
  CreateNodeParams,
  UpdateNodeParams,
  CreateEdgeParams,
  MarketResearchRunResponse,
  MarketResearchStatusResponse,
  SynthesisProposal,
  PromoteClusterInput,
  CapabilityProposal,
  PromoteCapabilityInput,
  AlignmentReport,
  AlignmentProposalRow,
  ApplyAlignmentInput,
  SetVerdictInput,
  ReattachRow,
  DeckProposal,
} from '@/types/api';
import type { Project, CreateProjectInput, UpdateProjectInput, ProjectMember, InviteMemberInput } from '@/types/project';
import type { MeResponse } from '@/types/account';
import type { DialogSession, CreateDialogInput, DialogRespondent, AddRespondentsInput, UpdateRespondentContextInput } from '@/types/dialog';
import { captureError } from '@/lib/error-tracking';
import { isCapturableHttpStatus } from '@/lib/error-scrub';

let config: { baseUrl: string } | null = null;

let authHeaderProvider: (() => Record<string, string>) | null = null;

export function setAuthHeaderProvider(fn: () => Record<string, string>) {
  authHeaderProvider = fn;
}

async function getBaseUrl(): Promise<string> {
  if (config) return config.baseUrl;
  const res = await fetch(`${import.meta.env.BASE_URL}config.json`);
  if (!res.ok) throw new Error(`Failed to load config: HTTP ${res.status}`);
  const cfg = await res.json();
  const baseUrl = cfg?.api?.baseUrl;
  if (typeof baseUrl !== 'string') throw new Error('Invalid config: missing api.baseUrl');
  config = { baseUrl };
  return config.baseUrl;
}

async function request<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const baseUrl = await getBaseUrl();
  const authHeaders = authHeaderProvider ? authHeaderProvider() : {};
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options?.headers,
    },
  });
  if (!res.ok) {
    // This layer returns {success:false} instead of throwing, so React Query
    // never sees HTTP failures — capture 5xx here or nowhere. Mutations that
    // re-throw will double-report through the mutation onError; accepted noise
    // until the API layer throws typed errors, which deletes this site.
    if (isCapturableHttpStatus(res.status)) {
      captureError(new Error(`HTTP ${res.status} ${path}`), {
        source: 'api-http',
        status: String(res.status),
      });
    }
    // Parse the JSON body if the server sent one. Lambda handlers
    // return structured error payloads (e.g. 409 conflict() carries
    // the in-flight researchId in `data`); without parsing here the
    // UI would just see "HTTP 409" with no actionable detail.
    try {
      const errorBody = (await res.json()) as { data?: unknown; message?: string } | null;
      return {
        success: false,
        data: (errorBody?.data ?? null) as T | null,
        message: errorBody?.message ?? `HTTP ${res.status}`,
      };
    } catch {
      return { success: false, data: null, message: `HTTP ${res.status}` };
    }
  }
  return res.json();
}

export interface DialogSessionRow {
  actorId: string;
  kind: 'invited' | 'anonymous';
  label: string;
  status: 'in progress' | 'unsubmitted' | 'submitted' | 'promoted';
  firstSeenAt: string;
  lastActiveAt: string;
  turnCount: number;
  draftItemCount: number;
  submittedAt?: string;
  promotedAt?: string;
  submittedItemCount?: number;
}
export interface SessionDraftItem {
  id: string;
  type: 'job' | 'challenge' | 'opportunity';
  title: string;
  description?: string;
  parentJobIds?: string[];
}

export const api = {
  // The signed-in caller's account (plan, limits, usage) or null when they
  // are not on the list. Drives the dashboard's request-access state.
  getMe: () => request<MeResponse>('/me'),

  // Projects
  listProjects: () => request<Project[]>('/projects'),
  getProject: (id: string) => request<Project>(`/projects/${id}`),
  createProject: (data: CreateProjectInput) =>
    request<Project>('/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: string, data: UpdateProjectInput) =>
    request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // Draft + persist the respondent framing from the project's aspiration.
  // framing:null with a reason is a soft outcome (nothing persisted), not an error.
  generateFraming: (id: string) =>
    request<{
      framing: { respondentFraming: string; openerBody: string } | null;
      reason?: 'no_concrete_subject' | 'generation_failed';
    }>(`/projects/${id}/generate-framing`, { method: 'POST' }),
  deleteProject: (id: string) =>
    request<void>(`/projects/${id}`, { method: 'DELETE' }),

  // Project members (collaboration). Invite/remove are owner-only server-side;
  // listing is open to any member. Keyed by email — no accept step.
  listMembers: (projectId: string) =>
    request<ProjectMember[]>(`/projects/${projectId}/members`),
  inviteMember: (projectId: string, data: InviteMemberInput) =>
    request<ProjectMember>(`/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify(data) }),
  removeMember: (projectId: string, email: string) =>
    request<void>(`/projects/${projectId}/members?email=${encodeURIComponent(email)}`, { method: 'DELETE' }),

  // Nodes
  listNodes: (projectId: string) => request<any[]>(`/projects/${projectId}/nodes`),
  createNode: (data: CreateNodeParams) =>
    request<any>('/nodes', { method: 'POST', body: JSON.stringify(data) }),
  updateNode: (data: UpdateNodeParams) =>
    request<any>(`/nodes/${data.id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteNode: (id: string, projectId: string) =>
    request<void>(`/nodes/${id}?projectId=${projectId}`, { method: 'DELETE' }),

  // Edges
  listEdges: (projectId: string) => request<any[]>(`/projects/${projectId}/edges`),
  createEdge: (data: CreateEdgeParams) =>
    request<any>('/edges', { method: 'POST', body: JSON.stringify(data) }),
  deleteEdge: (id: string, projectId: string) =>
    request<void>(`/edges/${id}?projectId=${projectId}`, { method: 'DELETE' }),

  // AI. Assumption generation is NOT here: it streams from the Lambda Function URL
  // (config.api.streamUrl), not this API-Gateway base URL. See use-assumptions.ts.

  // Market research. `runMarketResearch` kicks off Perplexity
  // Sonar deep research and returns within ~1s with a researchId; the
  // research itself completes minutes later, surfaced by polling
  // `getMarketResearchStatus`. The status endpoint also writes
  // signal nodes + edges on its first observation of Perplexity
  // COMPLETED — by the time the frontend sees status: 'complete' the
  // canvas data is already updated server-side.
  runMarketResearch: (projectId: string) =>
    request<MarketResearchRunResponse>('/market-research/run', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    }),
  getMarketResearchStatus: (projectId: string) =>
    request<MarketResearchStatusResponse>(`/market-research/${projectId}/latest`),

  // Synthesis (step 5). Generation itself is NOT here — it streams from the
  // Function URL (config.api.streamUrl), see use-synthesis.ts. These are the
  // proposal read + promote/dismiss curation on the API-Gateway base URL.
  getSynthesis: (projectId: string) =>
    request<SynthesisProposal | null>(`/projects/${projectId}/synthesis`),
  promoteCluster: (projectId: string, payload: PromoteClusterInput) =>
    request<any>(`/projects/${projectId}/synthesis/promote`, { method: 'POST', body: JSON.stringify(payload) }),
  dismissCluster: (projectId: string, clusterId: string) =>
    request<{ clusterId: string; status: 'dismissed' }>(`/projects/${projectId}/synthesis/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ clusterId }),
    }),
  // Accept a proposed assignment → attaches the item to its existing theme node.
  acceptAssignment: (projectId: string, assignmentId: string) =>
    request<{ assignmentId: string; status: 'accepted'; nodeId: string }>(`/projects/${projectId}/synthesis/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignmentId }),
    }),
  dismissAssignment: (projectId: string, assignmentId: string) =>
    request<{ assignmentId: string; status: 'dismissed' }>(`/projects/${projectId}/synthesis/dismiss-assignment`, {
      method: 'POST',
      body: JSON.stringify({ assignmentId }),
    }),

  // Capabilities (step 6, "what to build"). Generation streams from the Function
  // URL (config.api.streamUrl), see use-capabilities.ts. These are the proposal
  // read + promote/dismiss curation on the API-Gateway base URL.
  getCapabilities: (projectId: string) =>
    request<CapabilityProposal | null>(`/projects/${projectId}/capabilities`),
  promoteCapability: (projectId: string, payload: PromoteCapabilityInput) =>
    request<any>(`/projects/${projectId}/capabilities/promote`, { method: 'POST', body: JSON.stringify(payload) }),
  dismissCapability: (projectId: string, candidateId: string) =>
    request<{ candidateId: string; status: 'dismissed' }>(`/projects/${projectId}/capabilities/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ candidateId }),
    }),

  // Step 7 (pivot or persevere). The report is computed on demand from the
  // graph; the proposal row holds the AI's mapping and verdict suggestions and
  // apply/dismiss/reopen curate it. Hand corrections to the idea→capability
  // mapping still go through createEdge/deleteEdge with edgeType 'comprises'.
  getAlignment: (projectId: string) =>
    request<AlignmentReport>(`/projects/${projectId}/alignment`),
  getAlignmentProposal: (projectId: string) =>
    request<AlignmentProposalRow | null>(`/projects/${projectId}/alignment/proposal`),
  generateAlignment: (projectId: string) =>
    request<AlignmentProposalRow>(`/projects/${projectId}/alignment/generate`, { method: 'POST' }),
  applyAlignment: (projectId: string, input: ApplyAlignmentInput) =>
    request<unknown>(`/projects/${projectId}/alignment/apply`, { method: 'POST', body: JSON.stringify(input) }),
  dismissAlignment: (projectId: string, proposalId: string) =>
    request<{ proposalId: string; status: string }>(`/projects/${projectId}/alignment/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ proposalId }),
    }),
  reopenVerdict: (projectId: string, assumptionId: string, decidedAt?: string) =>
    request<{ assumptionId: string; reopened: boolean }>(`/projects/${projectId}/alignment/reopen`, {
      method: 'POST',
      body: JSON.stringify({ assumptionId, ...(decidedAt ? { decidedAt } : {}) }),
    }),
  setVerdict: (projectId: string, input: SetVerdictInput) =>
    request<unknown>(`/projects/${projectId}/alignment/verdict`, { method: 'POST', body: JSON.stringify(input) }),

  // Graph re-grounding (evidence re-attachment). generate runs the model and
  // writes the proposal; apply/dismiss curate it. apply moves a market_signal/
  // assumption edge off the idea onto the proposed node (so the canvas + the
  // alignment report change).
  getReattach: (projectId: string) =>
    request<ReattachRow | null>(`/projects/${projectId}/reattach`),
  generateReattach: (projectId: string) =>
    request<ReattachRow>(`/projects/${projectId}/reattach/generate`, { method: 'POST' }),
  applyReattach: (projectId: string, proposalId: string) =>
    request<unknown>(`/projects/${projectId}/reattach/apply`, {
      method: 'POST',
      body: JSON.stringify({ proposalId }),
    }),
  dismissReattach: (projectId: string, proposalId: string) =>
    request<{ proposalId: string; status: string }>(`/projects/${projectId}/reattach/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ proposalId }),
    }),

  // Pitch deck (step 8). Generation streams from the Function URL
  // (config.api.streamUrl), see use-deck.ts. This reads the persisted
  // DECK#LATEST; regenerate overwrites it, so there's no curation path.
  getDeck: (projectId: string) =>
    request<DeckProposal | null>(`/projects/${projectId}/deck`),


  // Dialogs (PM-facing)
  createDialog: (data: CreateDialogInput) =>
    request<DialogSession>('/dialogs', { method: 'POST', body: JSON.stringify(data) }),
  listDialogs: (projectId: string) =>
    request<DialogSession[]>(`/projects/${projectId}/dialogs`),
  deleteDialog: (id: string, projectId: string) =>
    request<void>(`/dialogs/${id}?projectId=${projectId}`, { method: 'DELETE' }),
  // Per-dialog org-framing toggle. The opener text itself is project-level
  // (step 4) and snapshotted at dialog creation — not editable per dialog.
  setDialogOrgFraming: (id: string, projectId: string, personaliseWithOrg: boolean) =>
    request<DialogSession>(`/dialogs/${id}?projectId=${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify({ personaliseWithOrg }),
    }),

  // Respondent management (PM-facing)
  addRespondents: (dialogId: string, data: AddRespondentsInput, projectId: string) =>
    request<DialogRespondent[]>(`/dialogs/${dialogId}/respondents?projectId=${projectId}`, { method: 'POST', body: JSON.stringify(data) }),
  listRespondents: (dialogId: string, projectId: string) =>
    request<DialogRespondent[]>(`/dialogs/${dialogId}/respondents?projectId=${projectId}`),
  resendInvite: (dialogId: string, respondentId: string, projectId: string) =>
    request<void>(`/dialogs/${dialogId}/respondents/${respondentId}/resend?projectId=${projectId}`, { method: 'POST' }),
  updateRespondentContext: (dialogId: string, respondentId: string, projectId: string, data: UpdateRespondentContextInput) =>
    request<DialogRespondent>(`/dialogs/${dialogId}/respondents/${respondentId}?projectId=${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  revokeRespondent: (dialogId: string, respondentId: string, projectId: string) =>
    request<void>(`/dialogs/${dialogId}/respondents/${respondentId}?projectId=${projectId}`, { method: 'DELETE' }),

  // Conversation sessions (PM-facing) — who talked, who submitted, and who
  // left a recoverable draft behind.
  listDialogSessions: (dialogId: string, projectId: string) =>
    request<DialogSessionRow[]>(`/dialogs/${dialogId}/sessions?projectId=${encodeURIComponent(projectId)}`),
  getDialogSessionDraft: (dialogId: string, actorId: string, projectId: string) =>
    request<{ draft: { items: SessionDraftItem[] } | null }>(
      `/dialogs/${dialogId}/sessions/${encodeURIComponent(actorId)}/draft?projectId=${encodeURIComponent(projectId)}`,
    ),
  promoteDialogSession: (dialogId: string, actorId: string, projectId: string) =>
    request<{ nodes: unknown[]; edges: unknown[] }>(
      `/dialogs/${dialogId}/sessions/${encodeURIComponent(actorId)}/promote?projectId=${encodeURIComponent(projectId)}`,
      { method: 'POST' },
    ),
};
